/**
 * CollapsibleBox — a titled SR-sidebar section that collapses to just its
 * header. Reuses the editor's configurable box style; collapse state is
 * persisted per section via the shared panel-layout store.
 */

import React, { useEffect } from 'react';
import { usePanelLayout, useSidebarOrder } from '../ui/panelLayoutStore';
import { color, font } from '../ui/theme';
import { IcTriRight, IcTriDown } from '../ui/icons';

const PANEL_DND_MIME = 'text/sr-panel';

export default function CollapsibleBox({
  id, title, boxStyle, defaultSidebar = 'right', headerAction, children,
}: {
  /** Stable id for persistence (usually the title). */
  id: string;
  title: string;
  /** The editor's configurable container style (interfaceSettings-driven). */
  boxStyle: React.CSSProperties;
  defaultSidebar?: 'left' | 'right';
  /** Optional control pinned to the RIGHT of the header (e.g. the Minimap's ⚙
   *  options button) — same slot as the Flow editor's Group. Absolutely
   *  positioned so the title stays centred, and its clicks never collapse. */
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}) {
  const collapsed = usePanelLayout(s => s.layouts.sr?.[id]?.collapsed ?? false);
  const order = usePanelLayout(s => s.layouts.sr?.[id]?.order ?? 0);
  const toggleCollapse = usePanelLayout(s => s.toggleCollapse);
  const register = usePanelLayout(s => s.register);
  const movePanel = usePanelLayout(s => s.movePanel);
  const sidebarIds = useSidebarOrder('sr', defaultSidebar);
  const [dragOver, setDragOver] = React.useState(false);
  useEffect(() => { register('sr', id, defaultSidebar, 0); }, [id, defaultSidebar, register]);

  return (
    <div
      style={{ ...boxStyle, order, ...(dragOver ? { outline: '2px dashed oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.7)', outlineOffset: 2 } : null) }}
      onDragOver={(e) => { if (e.dataTransfer.types.includes(PANEL_DND_MIME)) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        setDragOver(false);
        const dragged = e.dataTransfer.getData(PANEL_DND_MIME);
        if (!dragged || dragged === id) return;
        // Reorder within the same sidebar only.
        if (usePanelLayout.getState().get('sr', dragged)?.sidebar !== defaultSidebar) return;
        e.preventDefault(); e.stopPropagation();
        const idx = sidebarIds.indexOf(id);
        movePanel('sr', dragged, defaultSidebar, idx < 0 ? sidebarIds.length : idx);
      }}
    >
      <div
        draggable
        onDragStart={(e) => { e.dataTransfer.setData(PANEL_DND_MIME, id); e.dataTransfer.effectAllowed = 'move'; }}
        onClick={() => toggleCollapse('sr', id)}
        title="Click to collapse · drag to reorder"
        style={{ ...headerStyle, position: 'relative', cursor: 'grab', marginBottom: collapsed ? 0 : 1 }}
      >
        <span style={{ display: 'flex', alignItems: 'center', lineHeight: 1 }}>{collapsed ? <IcTriRight size={11} /> : <IcTriDown size={11} />}</span>
        <span style={labelStyle}>{title}</span>
        {headerAction && (
          <span
            style={{ position: 'absolute', right: 2, top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}
            onClick={(e) => e.stopPropagation()}
            draggable={false}
            onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
          >
            {headerAction}
          </span>
        )}
      </div>
      {!collapsed && children}
    </div>
  );
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  cursor: 'pointer',
  userSelect: 'none',
  color: color.accentSoft,
};

const labelStyle: React.CSSProperties = {
  // Matches Flow's groupLabelStyle so section titles read identically.
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: 1.4,
  textTransform: 'uppercase',
  fontFamily: font.sans,
};
